import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../';
import { Card, Typography, Alert, Table, Progress } from 'antd';
import {
    CheckCircleFilled,
    ExclamationCircleFilled,
    CloseCircleFilled,
    SyncOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import { useNotificationContext } from '../../providers/NotificationProvider';
import './SourceInspectionResults.scss';

const { Title, Text } = Typography;

/**
 * Source Inspection Results component that displays the results of source inspection
 *
 * @param {Array} selectedSources - Array of selected source objects
 * @param {Array} preflightResults - Array of preflight result objects
 * @param {boolean} loading - Whether the component is in loading state
 * @param {string} error - Error message if any
 */
export const SourceInspectionResults = ({
    selectedSources = [],
    preflightResults = [],
    loading = false,
    error = null,
}) => {
    const { notifySuccess } = useNotificationContext();

    // Calculate completion status
    const allComplete = preflightResults.every((result) => result.state === 'success' || result.state === 'error');

    // Get status icon (matches getMigrationReadiness from utils)
    const getStatusIcon = useCallback((status, errorType) => {
        switch (status) {
            case 'success':
                return <CheckCircleFilled className="status-icon success" title="Success" />;
            case 'error':
                if (errorType === 'warning') {
                    return <ExclamationCircleFilled className="status-icon warning" title="Warning" />;
                }
                return <CloseCircleFilled className="status-icon error" title="Error" />;
            case 'pending':
            default:
                return <SyncOutlined spin className="status-icon pending" title="In Progress" />;
        }
    }, []);

    // Calculate overall progress
    const getOverallProgress = useCallback(() => {
        if (!preflightResults.length) return 0;

        const completedCount = preflightResults.filter(
            (result) => result.state === 'success' || result.state === 'error',
        ).length;

        return Math.round((completedCount / preflightResults.length) * 100);
    }, [preflightResults]);

    // Table columns for source inspection results
    const columns = [
        {
            title: 'Source Name',
            key: 'name',
            render: (_, record) => {
                const source = selectedSources.find((s) => s.id === record.resource_id);
                return source ? source.name || source.hostname || source.host : 'Unknown';
            },
        },
        {
            title: 'Source IP',
            key: 'host',
            render: (_, record) => {
                const source = selectedSources.find((s) => s.id === record.resource_id);
                return source ? source.host : '-';
            },
        },
        {
            title: 'Type',
            key: 'type',
            render: (_, record) => {
                const source = selectedSources.find((s) => s.id === record.resource_id);
                return source ? source.collection_type || 'OS-Based' : '-';
            },
        },
        {
            title: 'Inspection Status',
            key: 'status',
            align: 'center',
            render: (_, record) => {
                return getStatusIcon(record.state, record.errorType);
            },
        },
        {
            title: 'Progress',
            key: 'progress',
            render: (_, record) => {
                if (record.state === 'success') return '100%';
                if (record.state === 'error') return 'Failed';
                if (record.state === 'pending') return 'In Progress...';
                return '-';
            },
        },
    ];

    // Handle download reports
    const handleDownloadPDF = useCallback(() => {
        // TODO: Implement PDF download
        notifySuccess({ description: 'PDF download feature coming soon' });
    }, [notifySuccess]);

    const handleDownloadCSV = useCallback(() => {
        // TODO: Implement CSV download
        notifySuccess({ description: 'CSV download feature coming soon' });
    }, [notifySuccess]);

    // Handle navigation back to sources
    const handleBackToSources = useCallback(() => {
        window.reactNavigate('/sources');
    }, []);

    if (error) {
        return (
            <div className="source-inspection-results">
                <Card>
                    <div className="error-state">
                        <Alert
                            title="Source Inspection Error"
                            description={error}
                            type="error"
                            showIcon
                            action={
                                <Button type="primary" onClick={handleBackToSources}>
                                    Back to Sources
                                </Button>
                            }
                        />
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="source-inspection-results">
            <Card>
                <div className="header-section">
                    <Title level={3}>Source Inspection Results</Title>
                    <Text className="description-text">
                        Inspecting {selectedSources.length} source{selectedSources.length > 1 ? 's' : ''} for detailed
                        information and compatibility
                    </Text>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <Text className="loading-text">Initiating source feature inspection...</Text>
                    </div>
                ) : (
                    <>
                        <div className="progress-section">
                            <Progress
                                percent={getOverallProgress()}
                                status={allComplete ? 'success' : 'active'}
                                format={(percent) => (allComplete ? 'Complete' : `${percent}%`)}
                            />
                        </div>

                        <Table
                            columns={columns}
                            dataSource={preflightResults}
                            rowKey="id"
                            pagination={false}
                            size="middle"
                        />

                        {allComplete && (
                            <div className="actions-section">
                                <div className="download-actions">
                                    <Button icon={<DownloadOutlined />} onClick={handleDownloadPDF}>
                                        Download PDF
                                    </Button>
                                    <Button icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
                                        Download CSV
                                    </Button>
                                </div>
                                <div className="navigation-actions">
                                    <Button type="primary" onClick={handleBackToSources}>
                                        Back to Sources
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
};
